import { useCallback, useRef } from "react";
import type { TableRow } from "../../types";
import type { DataTableCacheOptions, SortedColumn } from "../types";

const DEFAULT_CACHE_MAX_SIZE = 15;

/**
 * Hook for caching resolved page data in DataTable.
 *
 * Features:
 * - Caches resolved page data per page index using a Map stored in a ref
 * - Returns cached data synchronously on cache hit, avoiding unnecessary API calls
 * - Synchronously clears cache during render when invalidation triggers change
 * - Size-based eviction (FIFO) when cache exceeds maxSize: oldest entry is dropped to make room
 *
 * @param config - Configuration including loadPageData function, cache options, and invalidation triggers
 * @returns Object containing getOrLoad function and clearCache function
 */
export function usePageDataCache<T>(config: {
  loadPageData?: (params: {
    pageIndex: number;
    pageSize: number;
    sorting?: SortedColumn;
  }) => TableRow<T>[] | Promise<TableRow<T>[]>;
  cache?: DataTableCacheOptions;
  refreshKey?: string | number;
  refreshCounter: number;
  rowsPerPage: number;
  sorting?: SortedColumn;
}): {
  getOrLoad: (
    pageIndex: number,
    pageSize: number,
    sorting?: SortedColumn,
  ) => TableRow<T>[] | Promise<TableRow<T>[]>;
  clearCache: () => void;
} {
  const { loadPageData, cache: cacheConfig } = config;

  const cacheEnabled = cacheConfig?.enabled !== false;
  const cacheMaxSize = cacheConfig?.maxSize ?? DEFAULT_CACHE_MAX_SIZE;

  const pageCacheRef = useRef(new Map<number, TableRow<T>[]>());
  const loadPageDataRef = useRef(loadPageData);
  loadPageDataRef.current = loadPageData;

  // Generation counter to prevent stale in-flight promises from writing to the cache
  // after an invalidation has occurred. Incremented on every cache clear.
  const generationRef = useRef(0);

  // Synchronous cache invalidation during render:
  // Compare previous values of invalidation triggers and clear cache when any change.
  // This runs before useMemo in the consuming component, ensuring stale data is never returned.
  const prevRefreshKeyRef = useRef(config.refreshKey);
  const prevRefreshCounterRef = useRef(config.refreshCounter);
  const prevRowsPerPageRef = useRef(config.rowsPerPage);
  const prevSortingRef = useRef(config.sorting);

  if (
    prevRefreshKeyRef.current !== config.refreshKey ||
    prevRefreshCounterRef.current !== config.refreshCounter ||
    prevRowsPerPageRef.current !== config.rowsPerPage ||
    prevSortingRef.current?.id !== config.sorting?.id ||
    prevSortingRef.current?.desc !== config.sorting?.desc
  ) {
    pageCacheRef.current = new Map();
    generationRef.current += 1;
    prevRefreshKeyRef.current = config.refreshKey;
    prevRefreshCounterRef.current = config.refreshCounter;
    prevRowsPerPageRef.current = config.rowsPerPage;
    prevSortingRef.current = config.sorting;
  }

  const getOrLoad = useCallback(
    (
      pageIndex: number,
      pageSize: number,
      sorting?: SortedColumn,
    ): TableRow<T>[] | Promise<TableRow<T>[]> => {
      if (cacheEnabled) {
        const cached = pageCacheRef.current.get(pageIndex);
        if (cached) return cached;
      }

      const loader = loadPageDataRef.current;
      if (!loader) return [];

      const result = loader({ pageIndex, pageSize, sorting });

      if (!cacheEnabled) return result;

      // Capture the current generation so the promise callback can check
      // whether the cache was invalidated while the request was in-flight.
      const requestGeneration = generationRef.current;

      if (result instanceof Promise) {
        return result.then((data) => {
          if (generationRef.current !== requestGeneration) return data;
          const cache = pageCacheRef.current;
          if (cache.size >= cacheMaxSize) {
            const oldest = cache.keys().next().value;
            if (oldest != null) cache.delete(oldest);
          }
          cache.set(pageIndex, data);
          return data;
        });
      }

      const cache = pageCacheRef.current;
      if (cache.size >= cacheMaxSize) {
        const oldest = cache.keys().next().value;
        if (oldest != null) cache.delete(oldest);
      }
      cache.set(pageIndex, result);
      return result;
    },
    [cacheEnabled, cacheMaxSize],
  );

  const clearCache = useCallback(() => {
    pageCacheRef.current = new Map();
    generationRef.current += 1;
  }, []);

  return { getOrLoad, clearCache };
}
